import { useMutation } from '@tanstack/react-query'
import { httpClient } from '../../shared/api/httpClient'
import { useAstreaEnvironment } from '../../shared/hooks/useAstreaEnvironment'

/**
 * Port of `app/logoutUsers/logoutUsersService.js`'s
 * `enqueueSessionInvalidator(registrationType)` to a TanStack Query
 * mutation hook.
 *
 * Legacy behavior (confirmed by reading the AngularJS source):
 *
 *   logoutUsersService.enqueueSessionInvalidator = function(registrationType) {
 *     return $http({
 *       method: 'POST',
 *       url: urlAstrea + '/api/v2/dev/session-invalidator/enqueue',
 *       data: { cursor: "", registrationType: registrationType }
 *     });
 *   };
 *
 * `urlAstrea` there is `AuthService.getUrlAstreaPath()` — the base URL of
 * the *external* Astrea backend for the user's selected environment (LOCAL/
 * HOMOLOG/PROD/PLAYGROUND; see `app/register/authService.js`), not
 * this repo's own `server_app`. There is no matching route in `server_app/`
 * (confirmed via `grep -rn "session-invalidator" server_app/` — no hits):
 * this call goes straight from the browser to the external Astrea service,
 * exactly like the legacy app did with plain `$http`.
 *
 * The legacy app's `HttpInterceptor` (`app/interceptors/httpInterceptor.js`)
 * is registered globally on `$httpProvider`, so it also attached the fixed
 * servlet Basic Auth header to *this* cross-origin call. `httpClient`
 * (task 008) mirrors that same header unconditionally in its own request
 * interceptor regardless of target host, so reusing `httpClient` here
 * (passing the absolute Astrea URL as the request URL, which axios uses
 * as-is instead of prefixing its configured `baseURL` — see
 * https://axios-http.com/docs/req_config, `url` "will be prepended to
 * `baseURL` unless `url` is absolute") reproduces that behavior without
 * duplicating interceptor logic in a second axios instance. The `X-Session-
 * Id` header `httpClient` also attaches is extra vs. the legacy call (which
 * relied on the `sessionId` cookie alone), but harmless here since it is
 * just an additional identifying header sent to an endpoint that doesn't
 * inspect it for this action's cookie-less, cross-origin request.
 *
 * `getUrlAstreaPath` (`client/src/shared/context/astreaEnvironments.js`) is
 * the already-ported equivalent of `authService.getUrlAstreaPath()`'s
 * switch; `useAuth().getUrlAstrea()` is the already-ported equivalent of the
 * legacy `urlAstrea` cookie read inside that same legacy method.
 */
export function useLogoutUsersMutation() {
  const { urlAstreaPath: baseUrl } = useAstreaEnvironment()

  return useMutation({
    mutationFn: async (registrationType) => {
      const response = await httpClient.post(`${baseUrl}/api/v2/dev/session-invalidator/enqueue`, {
        cursor: '',
        registrationType,
      })
      return response.data
    },
  })
}

export default useLogoutUsersMutation
